import { useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/es";
import { Timestamp } from "firebase/firestore";
import { updateEnvio } from "../../firebase/firestore/envios";
import type { Envio } from "../../firebase/firestore/envios";
import { useEstados } from "../../utils/estadosUtils";
import { useEnvioStore } from "../../zustand/useEnviosStore";

dayjs.locale("es");

export default function FormShipment({
  onClose,
  entity,
}: {
  onClose: () => void;
  entity: Envio;
}) {
  const [nombre, setNombre] = useState(entity.nombre);
  const [fechaDesde, setFechaDesde] = useState<Dayjs>(
    dayjs(entity.fechaDesde.toDate())
  );
  const [fechaHasta, setFechaHasta] = useState<Dayjs>(
    dayjs(entity.fechaHasta.toDate())
  );
  const [estado, setEstado] = useState<
    "recibido" | "en_transito" | "listo_para_retirar" | "entregado"
  >(entity.estado as "recibido" | "en_transito" | "listo_para_retirar" | "entregado");
  const [isLoading, setIsLoading] = useState(false);
  const { fetchAllEnvios } = useEnvioStore();

  const handleSave = async () => {
    if (!nombre.trim()) {
      toast.error("Debe ingresar un nombre para el envío");
      return;
    }

    if (fechaDesde.isAfter(fechaHasta)) {
      toast.error("La fecha de inicio no puede ser posterior a la fecha de fin");
      return;
    }

    setIsLoading(true);

    try {
      await updateEnvio(
        entity.id,
        {
          nombre: nombre.trim(),
          fechaDesde: Timestamp.fromDate(fechaDesde.startOf("day").toDate()),
          fechaHasta: Timestamp.fromDate(fechaHasta.endOf("day").toDate()),
          estado,
        },
        entity
      );

      toast.success("Envío actualizado con éxito");
      await fetchAllEnvios();
      onClose();
    } catch (error) {
      console.error("Error actualizando envío:", error);
      toast.error("Error al actualizar el envío. Intente nuevamente");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Divider />
      <Box display="flex" flexDirection="column" gap={2} mt={2} px={2}>
        <TextField
          label="Nombre del envío"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          fullWidth
          required
        />

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <Box sx={{ display: "flex", gap: 2 }}>
            <DatePicker
              label="Desde"
              value={fechaDesde}
              format="DD/MM/YYYY"
              onChange={(newValue) => {
                if (newValue) {
                  setFechaDesde(newValue);
                  if (newValue.isAfter(fechaHasta)) {
                    setFechaHasta(newValue);
                  }
                }
              }}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <DatePicker
              label="Hasta"
              value={fechaHasta}
              format="DD/MM/YYYY"
              onChange={(newValue) => {
                if (newValue) {
                  if (newValue.isBefore(fechaDesde)) {
                    setFechaDesde(newValue);
                  }
                  setFechaHasta(newValue);
                }
              }}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Box>
        </LocalizationProvider>

        <Alert severity="warning" sx={{ mt: 1 }}>
          <strong>Precaución:</strong> Los cambios de estado al envío se aplicarán de forma masiva a todos los paquetes incluidos en el corte.
        </Alert>

        <FormControl fullWidth>
          <InputLabel>Estado</InputLabel>
          <Select
            value={estado}
            onChange={(e) =>
              setEstado(
                e.target.value as
                  | "recibido"
                  | "en_transito"
                  | "listo_para_retirar"
                  | "entregado"
              )
            }
            label="Estado"
            size="small"
          >
            {useEstados().map((estado) => (
              <MenuItem key={estado.id} value={estado.id}>
                {estado.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Divider sx={{ marginTop: 2 }} />
      <Box display="flex" gap={2} p={2}>
        <Button onClick={onClose} sx={{ mr: 1 }}>
          Cancelar
        </Button>
        <Box sx={{ flex: "1 1 auto" }} />
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !nombre.trim()}
        >
          {isLoading ? "Guardando..." : "Guardar"}
        </Button>
      </Box>
    </>
  );
}
